import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WorkflowCondition {
  id:        string
  field:     string
  operator:  string
  value:     string | null
  sortOrder: number
}

export interface WorkflowAction {
  id:         string
  actionType: string
  paramsJson: string
  sortOrder:  number
}

export interface WorkflowRule {
  id:             string
  name:           string
  triggerEvent:   string
  conditionLogic: string
  conditions:     WorkflowCondition[]
  actions:        WorkflowAction[]
  isActive:       boolean
  createdAt:      string
  updatedAt:      string
}

export interface CreateConditionDto {
  field:      string
  operator:   string
  value?:     string
  sortOrder?: number
}

export interface CreateActionDto {
  actionType:  string
  paramsJson?: string
  sortOrder?:  number
}

export interface CreateWorkflowPayload {
  name:            string
  triggerEvent:    string
  conditionLogic?: string
  conditions?:     CreateConditionDto[]
  actions?:        CreateActionDto[]
  /** SuperAdmin only — explicit org to create the workflow under. */
  orgId?:          string
}

export interface UpdateWorkflowPayload {
  name?:           string
  triggerEvent?:   string
  conditionLogic?: string
  conditions?:     CreateConditionDto[]
  actions?:        CreateActionDto[]
  isActive?:       boolean
  /** SuperAdmin only — reassign workflow to a specific org. */
  orgId?:          string
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const TRIGGER_EVENTS = [
  'LEAD_CREATED', 'LEAD_UPDATED', 'LEAD_ASSIGNED',
  'CALL_COMPLETED', 'CALL_MISSED',
  'DEAL_CREATED', 'DEAL_WON', 'DEAL_LOST',
  'TASK_OVERDUE', 'CUSTOMER_CREATED',
  'WHATSAPP_RECEIVED', 'EMAIL_RECEIVED',
] as const

export const TRIGGER_LABELS: Record<string, string> = {
  LEAD_CREATED:      'Lead Created',
  LEAD_UPDATED:      'Lead Updated',
  LEAD_ASSIGNED:     'Lead Assigned',
  CALL_COMPLETED:    'Call Completed',
  CALL_MISSED:       'Call Missed',
  DEAL_CREATED:      'Deal Created',
  DEAL_WON:          'Deal Won',
  DEAL_LOST:         'Deal Lost',
  TASK_OVERDUE:      'Task Overdue',
  CUSTOMER_CREATED:  'Customer Created',
  WHATSAPP_RECEIVED: 'WhatsApp Received',
  EMAIL_RECEIVED:    'Email Received',
}

export const CONDITION_OPERATORS = [
  { value: 'EQUALS',       label: 'Equals' },
  { value: 'NOT_EQUALS',   label: 'Not Equals' },
  { value: 'GREATER_THAN', label: 'Greater Than' },
  { value: 'LESS_THAN',    label: 'Less Than' },
  { value: 'CONTAINS',     label: 'Contains' },
  { value: 'NOT_CONTAINS', label: 'Not Contains' },
  { value: 'IN',           label: 'In (comma-separated)' },
  { value: 'NOT_IN',       label: 'Not In (comma-separated)' },
  { value: 'IS_EMPTY',     label: 'Is Empty' },
  { value: 'IS_NOT_EMPTY', label: 'Is Not Empty' },
]

export const NO_VALUE_OPERATORS = new Set(['IS_EMPTY', 'IS_NOT_EMPTY'])

export const OPERATOR_SYMBOL: Record<string, string> = {
  EQUALS:       '=',
  NOT_EQUALS:   '≠',
  GREATER_THAN: '>',
  LESS_THAN:    '<',
  CONTAINS:     'contains',
  NOT_CONTAINS: '!contains',
  IN:           'in',
  NOT_IN:       'not in',
  IS_EMPTY:     'is empty',
  IS_NOT_EMPTY: 'is not empty',
}

export const ACTION_TYPES = [
  { value: 'AUTO_ASSIGN',         label: 'Auto Assign' },
  { value: 'SEND_EMAIL',          label: 'Send Email' },
  { value: 'SEND_WHATSAPP',       label: 'Send WhatsApp' },
  { value: 'CREATE_TASK',         label: 'Create Task' },
  { value: 'UPDATE_STATUS',       label: 'Update Status' },
  { value: 'ADD_TAG',             label: 'Add Tag' },
  { value: 'REMOVE_TAG',          label: 'Remove Tag' },
  { value: 'SEND_NOTIFICATION',   label: 'Send Notification' },
  { value: 'ESCALATE_TO_MANAGER', label: 'Escalate to Manager' },
  { value: 'CREATE_NOTE',         label: 'Create Note' },
  { value: 'MOVE_PIPELINE_STAGE', label: 'Move Pipeline Stage' },
]

export const ACTION_DEFAULT_PARAMS: Record<string, string> = {
  AUTO_ASSIGN:         '{"strategy":"ROUND_ROBIN"}',
  SEND_EMAIL:          '{"subject":"","template":""}',
  SEND_WHATSAPP:       '{"message":""}',
  CREATE_TASK:         '{"title":"Follow up","dueInDays":1}',
  UPDATE_STATUS:       '{"status":""}',
  ADD_TAG:             '{"tag":""}',
  REMOVE_TAG:          '{"tag":""}',
  SEND_NOTIFICATION:   '{"message":""}',
  ESCALATE_TO_MANAGER: '{"reason":""}',
  CREATE_NOTE:         '{"content":""}',
  MOVE_PIPELINE_STAGE: '{"stage":""}',
}

// ── API ───────────────────────────────────────────────────────────────────────

export const workflowsApi = {
  getAll: async (): Promise<WorkflowRule[]> => {
    const res = await axiosInstance.get<{ data: WorkflowRule[] }>('/workflows')
    return res.data.data
  },

  getById: async (id: string): Promise<WorkflowRule> => {
    const res = await axiosInstance.get<{ data: WorkflowRule }>(`/workflows/${id}`)
    return res.data.data
  },

  create: async (payload: CreateWorkflowPayload): Promise<{ id: string }> => {
    const res = await axiosInstance.post<{ data: { id: string } }>('/workflows', payload)
    return res.data.data
  },

  update: async (id: string, payload: UpdateWorkflowPayload): Promise<void> => {
    await axiosInstance.put(`/workflows/${id}`, payload)
  },

  toggle: async (id: string, isActive: boolean): Promise<void> => {
    await axiosInstance.patch(`/workflows/${id}/toggle`, { isActive })
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/workflows/${id}`)
  },
}
